import { LogOut, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";
import { cn } from "../../lib/utils";
import { assimagraLogo, assimagraLogoSmall } from "@/assets";
import { getNavItems } from "../../constants/navigation";
import { LanguageToggle } from "../LanguageToggle";
import { useLanguage } from "../../contexts/LanguageContext";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useSidebar } from "../../hooks/sidebar/useSidebar";

interface SidebarProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

export const Sidebar = ({ isCollapsed, onToggleCollapse }: SidebarProps) => {
  const { t } = useLanguage();
  const { isItemActive, handleLogout, handleNavigation, claims, claimsLoading } = useSidebar();
  const navItems = getNavItems(claims?.role);

  return (
    <div
      className={cn(
        "fixed left-0 h-full transition-all duration-300 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/75 z-20",
        isCollapsed ? "w-16" : "w-56"
      )}
    >
      <div className="flex h-full flex-col">
        {/* Logo Section */}
        <div className={cn("flex h-20 items-center justify-center", isCollapsed ? "px-2" : "px-4")}>
          <div className="flex w-full items-center justify-between">
            {isCollapsed ? (
              <div className="flex items-center justify-center w-full">
                <img src={assimagraLogoSmall} alt="ASSIMAGRA" className="h-8 w-8" />
              </div>
            ) : (
              <img src={assimagraLogo} alt="ASSIMAGRA" className="h-10 w-auto" />
            )}
          </div>
        </div>

        {/* Navigation Items */}
        <ScrollArea className="flex-1 py-6">
          <div className="space-y-2.5 px-3 mt-2">
            {navItems.map((item) => (
              <TooltipProvider key={item.href}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      className={cn(
                        "w-full transition-all duration-200",
                        isCollapsed ? "justify-center h-11 px-0" : "justify-start h-11 px-4",
                        isItemActive(item) && [
                          "bg-gray-50/80 text-foreground font-medium shadow-[0_2px_8px_1px_rgba(0,0,0,0.08)] rounded-full",
                          !isCollapsed && "font-medium",
                        ],
                        "hover:bg-accent/50 hover:text-accent-foreground hover:scale-[1.02] rounded-full",
                        claimsLoading && "opacity-70 cursor-not-allowed"
                      )}
                      onClick={() => handleNavigation(item.href)}
                      disabled={claimsLoading}
                    >
                      <item.icon className={cn("h-5 w-5", !isCollapsed && "mr-3")} />
                      {!isCollapsed && <span>{t(item.titleKey)}</span>}
                    </Button>
                  </TooltipTrigger>
                  {isCollapsed && (
                    <TooltipContent side="right" align="center">
                      <p>{t(item.titleKey)}</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            ))}
          </div>
        </ScrollArea>

        {/* Logout and Language Toggle */}
        <div className="p-3">
          <div className={cn("flex items-center", isCollapsed ? "flex-col space-y-2" : "justify-between space-x-2")}>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    className={cn(
                      "h-11 transition-all duration-200",
                      isCollapsed ? "w-full justify-center px-0" : "flex-1 justify-start px-4",
                      "hover:bg-accent/50 hover:text-accent-foreground",
                      claimsLoading && "opacity-70 cursor-not-allowed"
                    )}
                    onClick={handleLogout}
                    disabled={claimsLoading}
                  >
                    <LogOut className={cn("h-5 w-5", !isCollapsed && "mr-3")} />
                    {!isCollapsed && <span>{t("navigation.logout")}</span>}
                  </Button>
                </TooltipTrigger>
                {isCollapsed && (
                  <TooltipContent side="right" align="center">
                    <p>{t("navigation.logout")}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
            <div
              className={cn(
                "flex items-center",
                isCollapsed ? "w-full justify-center scale-75 origin-center" : "justify-end"
              )}
            >
              <LanguageToggle />
            </div>
          </div>
        </div>

        {/* Floating Collapse Button */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="secondary"
                size="icon"
                onClick={onToggleCollapse}
                className={cn(
                  "absolute -right-4 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full",
                  "bg-white border border-teal-500/20",
                  "shadow-[0_2px_8px_1px_rgba(0,0,0,0.06)]",
                  "hover:shadow-[0_4px_16px_2px_rgba(0,0,0,0.08)]",
                  "transition-all duration-200",
                  "flex items-center justify-center",
                  "group"
                )}
              >
                {isCollapsed ? (
                  <ChevronRight className="h-4 w-4 text-teal-600 transition-colors" />
                ) : (
                  <ChevronLeft className="h-4 w-4 text-teal-600 transition-colors" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right" align="center">
              <p>{isCollapsed ? t("navigation.expand") : t("navigation.collapse")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
};
